'use client';

import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CheckCircle2, BookOpen } from 'lucide-react';
import Image from 'next/image';
import { Navbar, Footer } from '@/components/home';
import { Trophy as PhosphorTrophy } from '@phosphor-icons/react';
import Link from 'next/link';
import { useTranslations, useLocale } from '@/i18n';

// Fade Up Animation Variant
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true },
};

export default function AcademyPage() {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const t = useTranslations();
  const locale = useLocale();

  const academy = t.academy;

  return (
    <main className="min-h-screen bg-[#F8F7F4] dark:bg-black overflow-hidden selection:bg-brand-red selection:text-white transition-colors duration-500">
      <Navbar locale={locale} translations={t.nav} />

      {/* HERO SECTION */}
      <section className="relative min-h-[95vh] flex flex-col justify-center items-center pt-20 px-6">
        {/* Subtle Grain & Grid */}
        <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none mix-blend-multiply dark:mix-blend-overlay" />

        <div className="relative z-10 container max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            className="lg:col-span-6 flex flex-col items-center lg:items-start text-center lg:text-left"
            style={{ y: heroY, opacity: heroOpacity }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-3 mb-8"
            >
              <span className="h-px w-8 bg-black dark:bg-white" />
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-black dark:text-white/70">
                {academy.sectionLabel}
              </span>
            </motion.div>

            {/* Hero text animates position only: opacity-gating the LCP element
                hides it until hydration and tanks LCP on throttled devices */}
            <motion.h1
              initial={{ y: 30 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-[12vw] lg:text-[7.5rem] leading-[0.85] font-display font-medium tracking-tighter text-black dark:text-white mb-8"
            >
              {academy.heroTitle} <br />
              <span className="font-serif italic font-light text-brand-gold-text dark:text-brand-gold">
                {academy.heroHighlight}
              </span>
            </motion.h1>

            <motion.p
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg md:text-xl text-zinc-600 dark:text-zinc-200 max-w-lg leading-relaxed mb-10 font-sans"
            >
              {academy.heroDescription}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-wrap gap-4 justify-center lg:justify-start"
            >
              <Link href={`/${locale}/academy/waitlist`}>
                <Button className="h-14 px-8 rounded-full bg-brand-red hover:bg-brand-red/90 text-white text-base font-bold shadow-lg shadow-brand-red/20">
                  {academy.joinWaitlist}
                </Button>
              </Link>
              <Button
                variant="outline"
                className="h-14 px-8 rounded-full border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 text-black dark:text-white text-base"
                asChild
              >
                <a href="#curriculum">{academy.exploreCurriculum}</a>
              </Button>
            </motion.div>
          </motion.div>

          {/* Hero Visual */}
          <motion.div
            className="lg:col-span-6 relative h-[600px] w-full hidden lg:block"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-brand-gold/20 to-transparent rounded-full blur-[100px] opacity-60" />
            <div className="relative w-full h-full rounded-[120px_0_120px_0] overflow-hidden shadow-2xl rotate-3 hover:rotate-0 transition-all duration-700">
              <Image
                src="/images/academy/hero-3.jpg"
                alt="School of Destiny Student"
                fill
                className="object-cover hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-12 left-8 right-8 text-white z-10">
                <div className="text-5xl font-display font-bold mb-2 text-brand-gold">
                  {academy.year}
                </div>
                <div className="text-xs uppercase tracking-[0.3em] text-white/80 font-bold">
                  {academy.yearTheme}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* RATIONALE SECTION - The Bridge */}
      <section className="py-24 bg-white dark:bg-zinc-950 relative z-20 overflow-hidden">
        {/* Marquee */}
        <div className="w-full overflow-hidden mb-24 border-y border-black/5 dark:border-white/5 py-12 bg-[#F8F7F4] dark:bg-black">
          <motion.div
            animate={{ x: [0, -1500] }}
            transition={{ repeat: Infinity, duration: 30, ease: 'linear' }}
            className="flex whitespace-nowrap gap-16 text-6xl md:text-9xl font-display font-bold text-black/45 dark:text-white uppercase"
          >
            <span>{academy.marqueeText}</span>
            <span>{academy.marqueeText}</span>
          </motion.div>
        </div>

        <div className="container mx-auto px-6 max-w-7xl" id="about" />
      </section>

      {/* CURRICULUM SECTION */}
      <section
        className="py-32 bg-white dark:bg-zinc-950 text-black dark:text-white rounded-[4rem] mx-4 md:mx-10 overflow-hidden relative border border-black/5 dark:border-none transition-colors duration-500"
        id="curriculum"
      >
        {/* Abstract Background Element */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-red/5 dark:bg-brand-red/5 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/2" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-end mb-24 gap-12">
            <div className="max-w-2xl">
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                className="text-brand-red dark:text-brand-gold uppercase tracking-[0.4em] text-xs font-bold mb-4 block"
              >
                {academy.corePillars}
              </motion.span>
              <motion.h2
                variants={fadeInUp}
                initial="initial"
                whileInView="whileInView"
                className="text-6xl md:text-8xl font-display font-medium leading-none"
              >
                {academy.curriculum}{' '}
                <span className="text-brand-red dark:text-brand-gold font-serif italic">
                  {academy.curriculumHighlight}
                </span>
              </motion.h2>
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.6 }}
              className="max-w-xs text-zinc-500 dark:text-zinc-200 font-light italic text-lg"
            >
              {academy.curriculumDescription}
            </motion.p>
          </div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {/* Column 1 - Leadership */}
            <motion.div
              variants={fadeInUp}
              className="group relative bg-[#F8F7F4] dark:bg-zinc-900/50 backdrop-blur-sm border border-black/5 dark:border-white/5 rounded-[3rem] p-12 hover:bg-brand-red transition-all duration-700 flex flex-col justify-between h-[600px] shadow-sm hover:shadow-2xl"
            >
              <div className="absolute top-10 right-10 opacity-5 dark:opacity-10 group-hover:opacity-20 transition-opacity">
                <PhosphorTrophy
                  size={140}
                  weight="duotone"
                  className="text-black dark:text-white"
                />
              </div>
              <div>
                <span className="text-brand-red dark:text-brand-gold group-hover:text-white/60 font-bold tracking-widest text-xs mb-8 block uppercase">
                  {academy.module01}
                </span>
                <h3 className="text-4xl font-display font-bold leading-tight group-hover:text-white transition-colors">
                  {academy.characterLeadership} <br />
                  {academy.leadership}
                </h3>
              </div>
              <div>
                <p className="text-zinc-600 dark:text-zinc-200 group-hover:text-white/80 mb-8 leading-relaxed text-lg">
                  {academy.characterDescription}
                </p>
                <div className="flex flex-wrap gap-2">
                  {[academy.discipline, academy.ethics, academy.influence].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="px-4 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-medium group-hover:border-white/30 group-hover:bg-white/10 transition-colors group-hover:text-white"
                      >
                        {tag}
                      </span>
                    )
                  )}
                </div>
              </div>
            </motion.div>

            {/* Column 2 - Finance (Tall/Contrast) */}
            <motion.div
              variants={fadeInUp}
              className="group relative bg-brand-gold text-black rounded-[3rem] p-12 flex flex-col justify-between h-[600px] shadow-xl hover:shadow-2xl transition-all duration-500"
            >
              <div>
                <span className="text-black/40 font-bold tracking-widest text-xs mb-8 block uppercase">
                  {academy.module02}
                </span>
                <div className="h-16 w-16 bg-black text-white rounded-3xl flex items-center justify-center mb-8 rotate-3 group-hover:rotate-0 transition-transform">
                  <BookOpen size={32} />
                </div>
                <h3 className="text-4xl font-display font-bold leading-tight mb-6">
                  {academy.financialIntelligence} <br />
                  {academy.intelligence}
                </h3>
                <p className="text-black/70 mb-8 leading-relaxed text-lg font-medium">
                  {academy.financeDescription}
                </p>
              </div>
            </motion.div>

            {/* Column 3 - Strategy & Business */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-col gap-8 h-[600px]"
            >
              <div className="flex-1 bg-[#F8F7F4] dark:bg-zinc-900/50 backdrop-blur-sm border border-black/5 dark:border-white/5 rounded-[3rem] p-10 hover:border-brand-gold/50 transition-all shadow-sm hover:shadow-md">
                <h3 className="text-2xl font-display font-bold text-brand-gold-text dark:text-brand-gold mb-4">
                  {academy.careerStrategy}
                </h3>
                <p className="text-zinc-600 dark:text-zinc-200 text-sm mb-6 font-light">
                  {academy.careerDescription}
                </p>
                <div className="w-full h-1.5 bg-black/5 dark:bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '75%' }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                    className="h-full bg-brand-gold"
                  />
                </div>
              </div>
              <div className="flex-1 bg-[#F8F7F4] dark:bg-zinc-900/50 backdrop-blur-sm border border-black/5 dark:border-white/5 rounded-[3rem] p-10 hover:border-brand-red/50 transition-all shadow-sm hover:shadow-md">
                <h3 className="text-2xl font-display font-bold text-brand-red mb-4">
                  {academy.businessStructure}
                </h3>
                <p className="text-zinc-600 dark:text-zinc-200 text-sm mb-6 font-light">
                  {academy.businessDescription}
                </p>
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-white dark:bg-zinc-800 border-2 border-[#F8F7F4] dark:border-black flex items-center justify-center text-[10px] font-bold text-black dark:text-white shadow-sm"
                    >
                      {i}
                    </div>
                  ))}
                  <div className="w-10 h-10 rounded-full bg-brand-red border-2 border-[#F8F7F4] dark:border-black flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                    +
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TRACKS SECTION */}
      <section className="py-32 container mx-auto px-6">
        <div className="max-w-4xl mb-24">
          <h2 className="text-5xl md:text-7xl font-display font-medium mb-8 text-black dark:text-white leading-[0.9]">
            {academy.choosePath} <br />
            <span className="italic font-serif text-brand-gold-text dark:text-brand-gold">
              {academy.developmentPath}
            </span>
          </h2>
          <div className="h-1 w-24 bg-brand-red rounded-full" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 xl:gap-24">
          {/* Track 01 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col p-12 bg-white dark:bg-zinc-900 border border-black/5 dark:border-white/5 rounded-[3.5rem] shadow-sm hover:shadow-xl transition-shadow"
          >
            <div className="text-xs font-bold uppercase tracking-[0.4em] text-zinc-400 dark:text-zinc-300 mb-8">
              {academy.path01}
            </div>
            <h3 className="text-5xl font-serif italic mb-8 text-black dark:text-white">
              {academy.openCommunity}
            </h3>
            <p className="text-xl text-zinc-600 dark:text-zinc-200 mb-10 leading-relaxed font-light">
              {academy.openCommunityDescription}
            </p>
            <ul className="space-y-6 mb-12 flex-grow">
              {[
                academy.monthlyMasterclasses,
                academy.resourceLibrary,
                academy.networkingEvents,
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-4 text-black dark:text-white/80 font-medium"
                >
                  <div className="h-6 w-6 rounded-full bg-brand-gold/20 flex items-center justify-center">
                    <CheckCircle2 size={14} className="text-brand-gold" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            {/* Visible text must be descriptive: Lighthouse link-text ignores aria-label */}
            <Link href={`/${locale}/academy/waitlist`}>
              <Button
                variant="outline"
                className="w-fit rounded-full px-10 py-7 border-black dark:border-white text-black dark:text-white hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black transition-colors text-base font-bold"
              >
                {academy.learnMoreOpenCommunity}
              </Button>
            </Link>
          </motion.div>

          {/* Track 02 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col relative p-12 bg-brand-red text-white rounded-[3.5rem] shadow-2xl overflow-hidden"
          >
            <div className="absolute top-10 right-10 bg-brand-gold text-black text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest">
              {academy.limitedIntake}
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.4em] text-white/40 mb-8">
              {academy.path02}
            </div>
            <h3 className="text-5xl font-serif italic mb-8">
              {academy.exclusiveCohort}
            </h3>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light">
              {academy.exclusiveDescription}
            </p>
            <ul className="space-y-6 mb-12 flex-grow">
              {[
                academy.weeklyIntensive,
                academy.assignedMentor,
                academy.capstoneProject,
                academy.accreditedCertificate,
              ].map((item) => (
                <li key={item} className="flex items-center gap-4 font-medium">
                  <div className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center">
                    <CheckCircle2 size={14} className="text-white" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            <Link href={`/${locale}/academy/waitlist`}>
              <Button className="w-fit rounded-full px-10 py-7 bg-white text-brand-red hover:bg-zinc-100 shadow-xl text-base font-black">
                {academy.getNotified}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-24 relative overflow-hidden" id="apply">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="relative rounded-[4rem] bg-brand-red text-white p-16 md:p-32 overflow-hidden shadow-2xl">
            {/* Background Texture */}
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none" />

            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
              className="absolute -top-1/2 -right-1/2 w-[1200px] h-[1200px] border-[1px] border-white/10 rounded-full"
            />

            <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-3 py-2.5 px-6 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs font-black uppercase tracking-[0.2em] mb-10">
                  <span className="w-2.5 h-2.5 rounded-full bg-brand-gold animate-pulse" />
                  {academy.intakeLaunch}
                </div>
                <h2 className="text-6xl md:text-8xl font-display font-medium mb-10 leading-[0.85]">
                  {academy.readyToDominion} <br />
                  <span className="font-serif italic text-brand-gold">
                    {academy.takeDominion}
                  </span>
                </h2>
                <p className="text-2xl md:text-3xl text-white/80 max-w-xl mb-14 font-serif italic leading-relaxed">
                  &ldquo;{academy.scripture}&rdquo;
                </p>
                <div className="flex flex-wrap gap-6">
                  <Link href={`/${locale}/academy/waitlist`}>
                    <Button className="bg-white text-brand-red hover:bg-zinc-100 transition-all h-20 px-12 text-xl rounded-full font-black shadow-2xl">
                      {academy.joinWaitlist}
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="hidden lg:block relative h-[500px]">
                <div className="absolute top-10 right-10 w-72 h-96 bg-black/20 rounded-[3rem] rotate-12 backdrop-blur-sm" />
                <div className="absolute top-0 right-20 w-80 h-[450px] bg-white rounded-[3rem] overflow-hidden -rotate-6 shadow-2xl border-8 border-white">
                  <Image
                    src="/images/academy/hero-1.jpg"
                    alt="Student"
                    fill
                    className="object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
